import clsx from "clsx";
import { FunctionComponent, PropsWithChildren } from "react";

import { SELECTORS } from "@ui/config";
import { useComponentConfig } from "@ui/context/component-config-context";
import useDefaults from "@ui/hooks/use-defaults";
import {
  formatHubDate,
  formatBylineDate,
  formatCycloneDate,
  formatStreamDate,
  formatBreakerDate,
  formatNotificationsDate,
  formatQuickPostPermalinkDate,
  formatIn5GameDate,
  formatMmaEventDate,
  formatCoralAbsoluteDate,
} from "@voxmedia/duet-utils/date";

import * as styles from "./Timestamp.css";

export interface MetaTimestampBaseConfig {
  format:
    | "hub"
    | "breaker"
    | "lede"
    | "stream"
    | "latestRecirc"
    | "relatedQuickPost"
    | "quickPostPermalink"
    | "cyclone"
    | "notifications"
    | "in5"
    | "mmaEvent"
    | "coralAbsolute";
}

interface Props extends Partial<MetaTimestampBaseConfig> {
  timestamp: string;
  className?: string;
  componentContext?: string;
}

const Timestamp: FunctionComponent<PropsWithChildren<Props>> = ({
  timestamp,
  className,
  children,
  componentContext,
  ...props
}) => {
  const formatMappings = {
    hub: formatHubDate,
    breaker: formatHubDate,
    lede: formatBylineDate,
    stream: formatStreamDate,
    latestRecirc: formatHubDate,
    relatedQuickPost: formatBreakerDate,
    quickPostPermalink: formatQuickPostPermalinkDate,
    cyclone: formatCycloneDate,
    notifications: formatNotificationsDate,
    in5: formatIn5GameDate,
    mmaEvent: formatMmaEventDate,
    coralAbsolute: formatCoralAbsoluteDate,
  };

  const metaTimestampConfig = useComponentConfig(
    "metaTimestamp",
    componentContext || "",
  );

  const { format } = useDefaults(props, metaTimestampConfig);
  const formattedTimestamp = formatMappings[format](timestamp);

  return (
    <span
      className={clsx(SELECTORS.Timestamp, styles.timestamp[format], className)}
    >
      {children}
      <time dateTime={timestamp} suppressHydrationWarning>
        {formattedTimestamp}
      </time>
    </span>
  );
};

export default Timestamp;
